import numberWithCommas from 'common/util/numberWithCommas';

import type { DefaultIdeaField } from 'common/api/endpoints/idea';
import type { ViewSort } from 'common/api/endpoints/view';

export const MaxIdeaTitleLength = 400;
export const MaxIdeaDescriptionLength = 5000;
export const MaxIdeaTitleErrorMessage = `Title must be between 1 and ${numberWithCommas(
  MaxIdeaTitleLength,
)} characters`;
export const MaxIdeaDescriptionErrorMessage = `Failed to update description (${numberWithCommas(
  MaxIdeaDescriptionLength,
)} characters max)`;

export enum IdeaFieldTypes {
  boolean = 'boolean',
  date = 'date',
  dropdown = 'dropdown',
  fibonacci = 'fibonacci',
  formula = 'formula',
  number = 'number',
  multilineText = 'multilineText',
  multiselect = 'multiselect',
  rating = 'rating',
  score = 'score',
  text = 'text',
  user = 'user',
  url = 'url',
  range = 'range',
}

// Modes for displaying custom idea fields
export enum FieldDisplayMode {
  standard = 'standard',
  percentage = 'percentage',
  currency = 'currency',
}

export enum DefaultFieldNames {
  Author = 'author',
  Category = 'category',
  Created = 'created',
  Group = 'group',
  Owner = 'owner',
  Status = 'status',
  Themes = 'themes',
  Title = 'title',
  Type = 'type',
}

export enum IdeaImpactFieldNames {
  companies = 'companies',
  monthlySpend = 'monthlySpend',
  users = 'users',
}

export enum IdeaSpamStatus {
  confirmedSpam = 'confirmedSpam',
  notSpam = 'notSpam',
  waitingForReview = 'waitingForReview',
}

export const AllUsersSegmentID = 'everyone';

// helper type to map IdeaFieldTypes to their corresponding TypeScript types
export interface FieldTypeMap {
  [IdeaFieldTypes.boolean]: boolean;
  [IdeaFieldTypes.date]: Date;
  [IdeaFieldTypes.dropdown]: string;
  [IdeaFieldTypes.formula]: number;
  [IdeaFieldTypes.fibonacci]: number;
  [IdeaFieldTypes.rating]: number;
  [IdeaFieldTypes.number]: number;
  [IdeaFieldTypes.multilineText]: string;
  [IdeaFieldTypes.multiselect]: string[];
  [IdeaFieldTypes.score]: number;
  [IdeaFieldTypes.text]: string;
  [IdeaFieldTypes.user]: string;
  [IdeaFieldTypes.url]: string;
  [IdeaFieldTypes.range]: number;
}

export type DefaultFieldTypeMap = {
  [DefaultFieldNames.Author]: IdeaFieldTypes.user;
  [DefaultFieldNames.Category]: IdeaFieldTypes.text;
  [DefaultFieldNames.Created]: IdeaFieldTypes.date;
  [DefaultFieldNames.Group]: IdeaFieldTypes.text;
  [DefaultFieldNames.Owner]: IdeaFieldTypes.user;
  [DefaultFieldNames.Status]: IdeaFieldTypes.text;
  [DefaultFieldNames.Themes]: IdeaFieldTypes.multiselect;
  [DefaultFieldNames.Title]: IdeaFieldTypes.text;
  [DefaultFieldNames.Type]: IdeaFieldTypes.text;
};

export type CustomIdeaFieldValue = FieldTypeMap[keyof FieldTypeMap];

export const CustomIdeaFieldTypesList = Object.values(IdeaFieldTypes).filter(
  (type) => ![IdeaFieldTypes.formula, IdeaFieldTypes.score].includes(type),
);

// TODO: when we have Database Models for status, tags etc. we should likely change these to be dropdowns
export const DefaultFields: Record<DefaultFieldNames, DefaultIdeaField> = {
  [DefaultFieldNames.Author]: {
    name: 'author',
    label: 'Author',
    type: IdeaFieldTypes.user,
    custom: false,
    immutable: true,
  },
  [DefaultFieldNames.Category]: {
    name: 'category',
    label: 'Category',
    type: IdeaFieldTypes.text,
    custom: false,
  },
  [DefaultFieldNames.Created]: {
    name: 'created',
    label: 'Created',
    type: IdeaFieldTypes.date,
    custom: false,
    immutable: true,
  },
  [DefaultFieldNames.Group]: {
    name: 'group',
    label: 'Group',
    type: IdeaFieldTypes.dropdown,
    custom: false,
    defaultSortKey: 0,
  },
  [DefaultFieldNames.Owner]: {
    name: 'owner',
    label: 'Owner',
    type: IdeaFieldTypes.user,
    custom: false,
    defaultSortKey: '\uFFFF',
  },
  [DefaultFieldNames.Status]: {
    name: 'status',
    label: 'Status',
    type: IdeaFieldTypes.multiselect,
    custom: false,
  },
  [DefaultFieldNames.Themes]: {
    name: 'themes',
    label: 'Themes',
    type: IdeaFieldTypes.multiselect,
    custom: false,
  },
  [DefaultFieldNames.Title]: {
    name: 'title',
    label: 'Title',
    type: IdeaFieldTypes.text,
    custom: false,
  },
  [DefaultFieldNames.Type]: {
    name: 'type',
    label: 'Type',
    type: IdeaFieldTypes.text,
    custom: false,
  },
} as const;

// The maximum number of options for a custom idea dropdown or multiselect field
export const MaxFieldOptions = 25;

// The maximum number of characters for a custom idea field option
export const MaxFieldOptionCharLength = 30;

export const IdeaFieldTypeConfig: Record<
  IdeaFieldTypes,
  {
    hasOptions: boolean;
  }
> = {
  [IdeaFieldTypes.dropdown]: {
    hasOptions: true,
  },
  [IdeaFieldTypes.multiselect]: {
    hasOptions: true,
  },
  [IdeaFieldTypes.range]: {
    hasOptions: true,
  },
  [IdeaFieldTypes.boolean]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.date]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.number]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.multilineText]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.rating]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.fibonacci]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.text]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.user]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.url]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.formula]: {
    hasOptions: false,
  },
  [IdeaFieldTypes.score]: {
    hasOptions: false,
  },
};

export const getFieldSortKey = (value: CustomIdeaFieldValue) => {
  if (Array.isArray(value)) {
    return value.join(',');
  } else if (typeof value === 'boolean') {
    return value ? 1 : 0;
  } else if (typeof value === 'number') {
    return value;
  } else if (typeof value === 'string') {
    return value;
  } else if (value instanceof Date) {
    return value;
  }

  throw new Error(`Invalid value: ${value}`);
};

export const DefaultIdeaSort: ViewSort = { field: '_id', direction: 'desc' };
